import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Joyride, ACTIONS, EVENTS, STATUS } from 'react-joyride';

const LEGACY_DASHBOARD_KEY = 'sketchario_tour_done';
const TOUR_KEY_PREFIX = 'sketchario_page_tour_done:';

const TOUR_CONFIGS = {
  dashboard: [
    {
      target: '[data-testid="nav-dashboard"]',
      placement: 'right',
      title: 'Dashboard',
      content: 'Qui gestisci clienti, campagne e progetti editoriali.',
    },
    {
      target: '[data-testid="dashboard-client-management"]',
      placement: 'bottom',
      title: 'Clienti',
      content: 'Ogni card rappresenta un Client Brain: vedi chi è, la campagna più recente, puoi crearne una nuova o cancellare il profilo cliente senza eliminare automaticamente i progetti.',
    },
    {
      target: '[data-testid="nav-wizard"]',
      placement: 'right',
      title: 'Nuovo progetto',
      content: 'Il Wizard crea il progetto. Nei nuovi progetti Pro, dopo la generazione, il flusso prosegue nel Design DNA.',
    },
    {
      target: '[data-testid="help-btn"]',
      placement: 'right',
      title: 'Guida',
      content: 'Apre la knowledge base ufficiale. Gli stessi contenuti alimentano i punti interrogativi contestuali e l’assistente AI.',
    },
    {
      target: '[data-testid="notifications-bell"]',
      placement: 'right',
      title: 'Notifiche',
      content: 'Qui trovi aggiornamenti e notifiche operative.',
    },
    {
      target: '[data-testid="nav-profile"]',
      placement: 'right',
      title: 'Profilo',
      content: 'Gestisci profilo, piano e informazioni dell’account.',
    },
  ],
  wizard: [
    {
      target: '[data-testid="wizard-v3-router"]',
      placement: 'bottom',
      title: 'Wizard',
      content: 'Qui crei il progetto e definisci le informazioni di base. Nei progetti Pro il flusso continua nel Design DNA prima della Content Board.',
    },
    {
      target: '[data-testid="wizard-client-identity-card"]',
      placement: 'right',
      title: 'Cliente e progetto',
      content: 'Definisci il cliente, il progetto/campagna, il settore e le informazioni che devono restare associate al lavoro.',
    },
    {
      target: '[data-testid="wizard-next-btn"]',
      placement: 'left',
      title: 'Continua',
      content: 'Prosegui nel Wizard. Gli step successivi raccolgono social opzionali, riferimenti visuali e Tone of Voice.',
    },
  ],
  content: [
    {
      target: '[data-testid="project-title"]',
      placement: 'bottom',
      title: 'Content Board',
      content: 'Questa è la main board del progetto: al centro lavori sui Content Master e sui contenuti.',
    },
    {
      target: '[data-testid="content-filter-bar"]',
      placement: 'bottom',
      title: 'Filtri e viste',
      content: 'Da qui passi tra Content Master, Calendario e le viste disponibili senza rigenerare nulla.',
    },
    {
      target: '[data-testid^="master-content-card-"]',
      placement: 'right',
      title: 'Content Master',
      content: 'Apri un Master per leggerlo, modificarlo e derivare Carousel, Reel, Prompted Reel e PDF.',
    },
    {
      target: '[data-testid="feed-strips"]',
      placement: 'top',
      title: 'Feed',
      content: 'La barra inferiore raccoglie News, Reddit e suggerimenti AI. Puoi espanderla senza uscire dalla board.',
    },
    {
      target: '[data-testid="toggle-right-panel"]',
      placement: 'left',
      title: 'Queue di pubblicazione',
      content: 'Apre il pannello laterale con pubblicazioni queued, retrying, published o failed.',
    },
    {
      target: '[data-testid="nav-dna"]',
      placement: 'right',
      title: 'Design DNA',
      content: 'Per i progetti Pro il Design DNA è il passaggio da completare prima di produrre in scala.',
    },
  ],
  dna: [
    {
      target: '[data-testid="dna-workspace"]',
      placement: 'bottom',
      title: 'Design DNA',
      content: 'Qui definisci l’identità operativa del progetto prima della produzione: Design, Tone of Voice e Personas.',
    },
    {
      target: '[data-testid="dna-tab-design"]',
      placement: 'bottom',
      title: 'Design',
      content: 'Controlla identità visuale, font, logo, template V3 e impostazioni ereditate dal cliente.',
    },
    {
      target: '[data-testid="dna-tab-tov"]',
      placement: 'bottom',
      title: 'Tone of Voice',
      content: 'Verifica come il brand deve parlare prima di generare e distribuire contenuti.',
    },
    {
      target: '[data-testid="dna-tab-personas"]',
      placement: 'bottom',
      title: 'Personas',
      content: 'Controlla a chi stai parlando. È il riferimento strategico per il messaggio.',
    },
    {
      target: '[data-testid="nav-content"]',
      placement: 'right',
      title: 'Continua ai contenuti',
      content: 'Quando il DNA è coerente, torna alla Content Board e lavora sui Master.',
    },
  ],
  connections: [
    {
      target: '[data-testid="connections-workspace"]',
      placement: 'bottom',
      title: 'Connessioni',
      content: 'Questo workspace riunisce social, crediti AI e connettori creativi.',
    },
    {
      target: '[data-testid="connections-tab-social"]',
      placement: 'bottom',
      title: 'Social',
      content: 'Collega e controlla i profili social usati per la pubblicazione.',
    },
    {
      target: '[data-testid="connections-tab-aiCredits"]',
      placement: 'bottom',
      title: 'Crediti AI',
      content: 'Controlla l’uso delle risorse AI disponibili sul piano.',
    },
    {
      target: '[data-testid="connections-tab-creativeTools"]',
      placement: 'bottom',
      title: 'Creative Tools',
      content: 'Qui trovi i connettori e gli strumenti esterni disponibili per i workflow creativi.',
    },
  ],
  media: [
    {
      target: '[data-testid="media-library-panel"]',
      placement: 'bottom',
      title: 'Media Library',
      content: 'Gestisci immagini e video del progetto. Alcuni output, come le foto PDF, usano upload dedicati invece della libreria generica.',
    },
  ],
  analytics: [
    {
      target: '[data-testid="analytics-title"]',
      placement: 'bottom',
      title: 'Analytics',
      content: 'Qui trovi le metriche restituite dai social collegati. Disponibilità e dettaglio dipendono dalle API dei provider.',
    },
  ],
  feeds: [
    {
      target: '[data-testid="feeds-page"]',
      placement: 'bottom',
      title: 'Feed',
      content: 'Questa pagina raccoglie le fonti di ispirazione del progetto e i suggerimenti AI.',
    },
    {
      target: '[data-testid="feeds-news-section"]',
      placement: 'bottom',
      title: 'News e Reddit',
      content: 'Qui trovi fonti esterne rilevanti per il settore e puoi aggiornarle senza uscire dal progetto.',
    },
    {
      target: '[data-testid="feeds-ai-section"]',
      placement: 'top',
      title: 'Suggerimenti AI',
      content: 'Questa sezione genera spunti editoriali coerenti con il progetto. Le funzioni disponibili dipendono dal piano.',
    },
    {
      target: '[data-testid="feed-strips"]',
      placement: 'top',
      title: 'Feed rapido',
      content: 'La stessa area Feed resta disponibile anche come barra inferiore compatta nelle altre viste del progetto.',
    },
  ],
};

const JOYRIDE_STYLES = {
  options: {
    zIndex: 10000,
    primaryColor: '#f472b6',
    backgroundColor: '#151d2e',
    textColor: '#ffffff',
    arrowColor: '#151d2e',
    overlayColor: 'rgba(0,0,0,.68)',
  },
  tooltip: {
    borderRadius: '1rem',
    border: '1px solid rgba(255,255,255,.1)',
    padding: '1.25rem',
    maxWidth: 330,
    boxShadow: '0 20px 60px rgba(0,0,0,.5)',
  },
  tooltipTitle: { fontSize: '.95rem', fontWeight: 700, marginBottom: '.5rem', color: '#fff' },
  tooltipContent: { fontSize: '.84rem', lineHeight: 1.65, color: '#a7b0c0', padding: 0 },
  buttonNext: {
    background: 'linear-gradient(135deg,#6366f1,#ec4899)',
    borderRadius: '.625rem',
    fontSize: '.8rem',
    fontWeight: 600,
    padding: '.5rem 1rem',
    border: 'none',
    color: '#fff',
  },
  buttonSkip: { color: '#7c8797', fontSize: '.8rem', background: 'transparent' },
  buttonBack: { color: '#a7b0c0', fontSize: '.8rem', marginRight: '.5rem' },
};

function pageTourKey(view) {
  return `${TOUR_KEY_PREFIX}${view || 'dashboard'}`;
}

export function hasTourForView(view) {
  return Boolean(TOUR_CONFIGS[view]);
}

export function shouldShowTour(status, view = 'dashboard') {
  if (!hasTourForView(view)) return false;
  // Workspace tours remain available from Help, but only the Dashboard tour
  // may auto-start. Auto-launching a new page tour while the user is entering
  // the Wizard masks the freshly opened interface and feels like a broken UI.
  if (view !== 'dashboard') return false;
  if (status?.product_tour_completed) return false;
  if (localStorage.getItem(LEGACY_DASHBOARD_KEY)) return false;
  return !localStorage.getItem(pageTourKey(view));
}

export function markTourDone(view = 'dashboard') {
  localStorage.setItem(pageTourKey(view), '1');
  if (view === 'dashboard') localStorage.setItem(LEGACY_DASHBOARD_KEY, '1');
}

export default function ProductTour({ api, activeView = 'dashboard', onFinish }) {
  const [run, setRun] = useState(false);
  const didFinishRef = useRef(false);
  const steps = useMemo(() => (TOUR_CONFIGS[activeView] || []).map(step => ({
    ...step,
    // Never leave a floating Joyride beacon over the app. Tours open their
    // tooltip directly and can always be dismissed from the tooltip itself.
    disableBeacon: true,
  })), [activeView]);

  useEffect(() => {
    didFinishRef.current = false;
    setRun(false);
    if (!steps.length) return undefined;
    let cancelled = false;
    let attempts = 0;
    const startWhenReady = () => {
      if (cancelled) return;
      const firstTarget = document.querySelector(steps[0].target);
      if (firstTarget) {
        setRun(true);
        return;
      }
      attempts += 1;
      if (attempts < 50) window.setTimeout(startWhenReady, 120);
    };
    startWhenReady();
    return () => { cancelled = true; };
  }, [activeView, steps]);

  const finishTour = useCallback(async () => {
    if (didFinishRef.current) return;
    didFinishRef.current = true;
    markTourDone(activeView);
    setRun(false);
    if (api && activeView === 'dashboard') {
      await api.post('/onboarding/product-tour').catch(() => {});
    }
    onFinish?.();
  }, [activeView, api, onFinish]);

  const handleCallback = useCallback((data) => {
    const { status, type, action } = data;
    if (
      [STATUS.FINISHED, STATUS.SKIPPED].includes(status)
      || type === EVENTS.TOUR_END
      || action === ACTIONS.CLOSE
    ) {
      finishTour();
    }
  }, [finishTour]);

  if (!steps.length) return null;

  return (
    <Joyride
      steps={steps}
      run={run}
      continuous
      showSkipButton
      showProgress
      scrollToFirstStep
      disableScrolling={false}
      locale={{
        back: 'Indietro',
        close: 'Chiudi',
        last: 'Fine',
        next: 'Avanti',
        open: 'Avanti',
        skip: 'Salta',
      }}
      styles={JOYRIDE_STYLES}
      callback={handleCallback}
      floaterProps={{ disableAnimation: true }}
    />
  );
}
